import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Tag } from "@/components/Layout";
import { Loader2, ListChecks, AlertTriangle, CalendarClock, Eye, Flag, TimerOff } from "lucide-react";

// Phase 84.62 — Project Insights: company-wide task/milestone rollup across
// every ACTIVE project. Lives inside the Projects hub page as a view toggle
// (Grid / Insights), not a separate route.
function OverallCard({ label, value, tone, icon: Icon, testId }) {
  return (
    <div className="card-flat p-4" data-testid={testId}>
      <div className="flex items-center justify-between mb-2">
        <span className="label-overline">{label}</span>
        {Icon && <Icon size={13} className="text-slate-400" />}
      </div>
      <div className={`font-display text-2xl sm:text-3xl font-bold ${tone || "text-slate-900"}`}>{value}</div>
    </div>
  );
}

function gateCountdown(days) {
  if (days === null || days === undefined) return null;
  if (days < 0) return { text: `overdue ${Math.abs(days)}d`, tone: "text-red-600" };
  if (days === 0) return { text: "due today", tone: "text-orange-600" };
  return { text: `${days}d left`, tone: "text-slate-500" };
}

export default function ProjectInsights() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    api.get("/project-insights")
      .then(({ data }) => { if (alive) setData(data); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  const overall = data?.overall || {};
  const statusTable = data?.status_table || [];
  const byProject = data?.by_project || [];
  const byOwner = data?.by_owner || [];

  const stale = data?.stale_projects || [];

  const statusTone = useMemo(() => ({
    planned: "slate", in_progress: "blue", review: "purple", done: "green",
  }), []);

  if (loading) {
    return (
      <div className="p-10 flex items-center justify-center text-slate-500" data-testid="project-insights-loading">
        <Loader2 size={18} className="animate-spin mr-2" /> Loading Project Insights…
      </div>
    );
  }

  return (
    <div data-testid="project-insights" className="space-y-6">
      {/* Overall Health */}
      <div>
        <div className="label-overline mb-2">Overall health · active projects</div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          <OverallCard label="Total tasks" value={overall.total_tasks ?? 0} icon={ListChecks} testId="insights-total-tasks" />
          <OverallCard label="% complete" value={`${overall.pct_complete ?? 0}%`} tone="text-[#F54800]" testId="insights-pct-complete" />
          <OverallCard label="Overdue" value={overall.overdue_tasks ?? 0} tone={overall.overdue_tasks > 0 ? "text-red-600" : "text-slate-900"} icon={AlertTriangle} testId="insights-overdue" />
          <OverallCard label="Due today / tmrw" value={`${overall.due_today ?? 0} / ${overall.due_tomorrow ?? 0}`} icon={CalendarClock} testId="insights-due-soon" />
          <OverallCard label="In review" value={overall.in_review_tasks ?? 0} tone={overall.in_review_tasks > 0 ? "text-purple-600" : "text-slate-900"} icon={Eye} testId="insights-in-review" />
        </div>
      </div>

      {/* Stale active projects — no activity in 10+ days */}
      {stale.length > 0 && (
        <div className="card-flat overflow-hidden border-amber-200" data-testid="insights-stale-projects">
          <div className="px-5 py-3 border-b border-amber-100 bg-amber-50 flex items-center gap-2">
            <TimerOff size={14} className="text-amber-600 shrink-0" />
            <div className="font-display font-bold text-sm text-amber-800">Stale projects · no activity in 10+ days</div>
          </div>
          <div className="divide-y divide-slate-100">
            {stale.map((s) => (
              <Link
                key={s.project_id}
                to={`/projects/${s.project_id}`}
                className="flex items-center justify-between px-5 py-2.5 hover:bg-amber-50/50"
                data-testid={`insights-stale-row-${s.project_id}`}
              >
                <span className="font-medium text-slate-900">{s.name}</span>
                <span className="text-xs font-mono text-amber-700">{s.days_inactive}d inactive</span>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Project Status Table */}
      <div className="card-flat overflow-hidden" data-testid="insights-status-table">
        <div className="px-5 py-3 border-b border-slate-100 font-display font-bold text-sm text-slate-900">Project status table</div>
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
            <tr>
              <th className="text-left px-5 py-2">Status</th>
              <th className="text-right px-5 py-2"># of tasks</th>
              <th className="text-right px-5 py-2">% of all</th>
              <th className="text-right px-5 py-2">P0 tasks</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {statusTable.map((row) => (
              <tr key={row.status} data-testid={`insights-status-row-${row.status}`}>
                <td className="px-5 py-2.5"><Tag tone={statusTone[row.status] || "slate"}>{row.label}</Tag></td>
                <td className="px-5 py-2.5 text-right font-mono">{row.count}</td>
                <td className="px-5 py-2.5 text-right font-mono text-slate-500">{row.pct}%</td>
                <td className="px-5 py-2.5 text-right font-mono">
                  {row.p0_count > 0 ? <span className="text-red-600 font-semibold">{row.p0_count}</span> : row.p0_count}
                </td>
              </tr>
            ))}
            {statusTable.length === 0 && (
              <tr><td colSpan={4} className="px-5 py-6 text-center text-slate-500">No tasks on any active project yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Progress by Project + Launch Gates + Red Alert */}
      <div className="card-flat overflow-hidden" data-testid="insights-by-project">
        <div className="px-5 py-3 border-b border-slate-100 font-display font-bold text-sm text-slate-900">
          Progress by project
          <span className="text-[11px] font-normal text-slate-500 ml-2">Red row = deadline ≤7 days with &gt;3 launch gates open, OR already past deadline with any gate open</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
              <tr>
                <th className="text-left px-5 py-2">Project</th>
                <th className="text-right px-5 py-2">Total tasks</th>
                <th className="text-right px-5 py-2">To do</th>
                <th className="text-right px-5 py-2">In progress</th>
                <th className="text-right px-5 py-2">Review</th>
                <th className="text-right px-5 py-2">Done</th>
                <th className="text-right px-5 py-2">% done</th>
                <th className="text-right px-5 py-2">Launch gates open</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {byProject.map((p) => (
                <tr key={p.project_id} className={p.red_alert ? "bg-red-50" : ""} data-testid={`insights-project-row-${p.project_id}`}>
                  <td className="px-5 py-2.5">
                    <Link to={`/projects/${p.project_id}`} className="hover:text-[#F54800] flex items-center gap-1.5">
                      {p.red_alert && <AlertTriangle size={12} className="text-red-600 shrink-0" />}
                      <span className={`font-medium ${p.red_alert ? "text-red-700" : "text-slate-900"}`}>{p.name}</span>
                    </Link>
                  </td>
                  <td className="px-5 py-2.5 text-right font-mono">{p.total_tasks}</td>
                  <td className="px-5 py-2.5 text-right font-mono text-slate-500">{p.status_counts?.planned ?? 0}</td>
                  <td className="px-5 py-2.5 text-right font-mono text-slate-500">{p.status_counts?.in_progress ?? 0}</td>
                  <td className="px-5 py-2.5 text-right font-mono text-slate-500">{p.status_counts?.review ?? 0}</td>
                  <td className="px-5 py-2.5 text-right font-mono text-slate-500">{p.status_counts?.done ?? 0}</td>
                  <td className="px-5 py-2.5 text-right font-mono">{p.pct_done}%</td>
                  <td className="px-5 py-2.5 text-right">
                    <span
                      className={`inline-flex items-center gap-1 font-mono px-1.5 py-0.5 rounded-sm ${p.red_alert ? "bg-red-100 text-red-700" : "bg-slate-100 text-slate-600"}`}
                      data-testid={`insights-launch-gates-${p.project_id}`}
                    >
                      <Flag size={10} /> {p.launch_gates_open}/{p.launch_gates_total}
                    </span>
                    {gateCountdown(p.days_to_deadline) && (
                      <div className={`text-[10px] font-mono mt-1 ${gateCountdown(p.days_to_deadline).tone}`} data-testid={`insights-gate-countdown-${p.project_id}`}>
                        {gateCountdown(p.days_to_deadline).text}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
              {byProject.length === 0 && (
                <tr><td colSpan={8} className="px-5 py-6 text-center text-slate-500">No active projects.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Performance by Owner */}
      <div className="card-flat overflow-hidden" data-testid="insights-by-owner">
        <div className="px-5 py-3 border-b border-slate-100 font-display font-bold text-sm text-slate-900">Performance by owner</div>
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
            <tr>
              <th className="text-left px-5 py-2">Team member</th>
              <th className="text-right px-5 py-2">Total tasks</th>
              <th className="text-right px-5 py-2">Open</th>
              <th className="text-right px-5 py-2">Done</th>
              <th className="text-right px-5 py-2">% done</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {byOwner.map((o) => (
              <tr key={o.user_id} data-testid={`insights-owner-row-${o.user_id}`}>
                <td className="px-5 py-2.5 font-medium text-slate-900">{o.name}</td>
                <td className="px-5 py-2.5 text-right font-mono">{o.total}</td>
                <td className="px-5 py-2.5 text-right font-mono text-slate-500">{o.open}</td>
                <td className="px-5 py-2.5 text-right font-mono text-slate-500">{o.done}</td>
                <td className="px-5 py-2.5 text-right font-mono">{o.pct_done}%</td>
              </tr>
            ))}
            {byOwner.length === 0 && (
              <tr><td colSpan={5} className="px-5 py-6 text-center text-slate-500">No tasks assigned yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
